import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Program } from "../../types/program";
import { useCart } from "../../context/CartContext";
import { useRefCodeManagement } from "../../hooks/useRefCodeManagement";

interface SidebarProps {
  program: Program;
  programId?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ program, programId }) => {
  const navigate = useNavigate();
  const { clearAndEnroll } = useCart();
  const { navigateWithRefCode } = useRefCodeManagement();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white rounded-2xl shadow-lg p-8 sticky top-8"
    >
      <div className="text-center mb-8">
        <div className="text-4xl font-bold text-blue-600 mb-2">
          {program.formatted_price}
        </div>
        <div className="text-gray-600">Duration: {program.duration}</div>
      </div>

      {program.available_tickets && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Availability
            </span>
            <span className="text-sm font-bold text-blue-600">
              {program.remaining_tickets} / {program.available_tickets} spots
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${
                  ((program.sold_tickets || 0) / program.available_tickets) *
                  100
                }%`,
              }}
            ></div>
          </div>
          {program.remaining_tickets && program.remaining_tickets <= 10 && (
            <p className="text-red-600 text-sm font-medium mt-2">
              ⚡ Limited spots remaining!
            </p>
          )}
        </div>
      )}

      <button
        onClick={() => {
          if (program) {
            // For Ivy League Negotiation Workshop, redirect to elite-workshops page to choose program format
            const programSlug = (program as any)?.slug || programId;
            if (
              programSlug === "ivy-league-negotiation-workshop" ||
              programSlug === "harvard-negotiation-workshop" ||
              program.title?.toLowerCase().includes("ivy league negotiation") ||
              program.title?.toLowerCase().includes("harvard negotiation")
            ) {
              navigate(
                "/elite-workshops/leadership-negotiation-communication#choose-format"
              );
              return;
            }
            // For other programs, add to cart as before
            clearAndEnroll(program, 1);
            navigate(navigateWithRefCode("/cart"));
          }
        }}
        className="w-full bg-gradient-to-r from-[#9f162e] to-[#1f2444] text-white font-bold py-4 px-6 rounded-xl hover:from-[#1f2444] hover:to-[#9f162e] transition-all duration-300 transform hover:scale-105 shadow-lg"
      >
        Enroll Now
      </button>

      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-center text-yellow-500 mb-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-current" />
          ))}
        </div>
        <p className="text-center text-sm text-gray-600">
          Highly rated by our students
        </p>
      </div>
    </motion.div>
  );
};

export default Sidebar;
